import { useState, useCallback } from 'react';

const useForm = ({ 
  initialValues = {}, 
  validationRules = {}, 
  formatters = {},
  onSubmit 
}) => {
  const [formData, setFormData] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Handle field change - support both event objects and direct name/value calls
  const handleChange = useCallback((nameOrEvent, directValue) => {
    let name, value;
    
    if (typeof nameOrEvent === 'string') {
      // Direct call: handleChange('fieldName', value)
      name = nameOrEvent;
      value = directValue;
    } else {
      // Event object: handleChange(event)
      const event = nameOrEvent;
      name = event.target.name;
      value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    }
  
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  
    // Add real-time validation for the changed field
    if (validationRules[name]) {
      const rules = validationRules[name];
      const fieldValue = value;
  
      // Required validation
      let isRequired = false;
      if (typeof rules.required === 'function') {
        isRequired = rules.required(formData);
      } else if (rules.required) {
        isRequired = true;
      }
      
      if (isRequired && (!fieldValue || fieldValue.toString().trim() === '')) {
        setErrors(prev => ({
          ...prev,
          [name]: rules.message || `Το πεδίο είναι υποχρεωτικό`
        }));
        return;
      }
  
      // Skip other validations if field is empty and not required
      if (!fieldValue || fieldValue.toString().trim() === '') {
        // But still check if it's required and show error
        if (isRequired) {
          setErrors(prev => ({
            ...prev,
            [name]: rules.message || `Το πεδίο είναι υποχρεωτικό`
          }));
        }
        return;
      }
  
      // Min length validation
      if (rules.minLength && fieldValue.toString().length < rules.minLength) {
        setErrors(prev => ({
          ...prev,
          [name]: rules.message || `Ελάχιστο μήκος: ${rules.minLength} χαρακτήρες`
        }));
        return;
      }
  
      // Max length validation
      if (rules.maxLength && fieldValue.toString().length > rules.maxLength) {
        setErrors(prev => ({
          ...prev,
          [name]: rules.message || `Μέγιστο μήκος: ${rules.maxLength} χαρακτήρες`
        }));
        return;
      }
  
      // Email validation
      if (rules.email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(fieldValue)) {
          setErrors(prev => ({
            ...prev,
            [name]: rules.message || 'Μη έγκυρη διεύθυνση email'
          }));
          return;
        }
      }
  
      // Custom validation function
      if (rules.validate && typeof rules.validate === 'function') {
        const customError = rules.validate(fieldValue, formData);
        if (customError) {
          setErrors(prev => ({
            ...prev,
            [name]: customError
          }));
          return;
        }
      }
  
      // Clear error if validation passes
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors, validationRules, formData]);

  // Validate form (optional data: use for submit after cleaning fields without waiting for React state)
  const validate = useCallback((dataOverride) => {
    const data = dataOverride !== undefined && dataOverride !== null ? dataOverride : formData;
    const newErrors = {};

    for (const fieldName of Object.keys(validationRules)) {
      const rules = validationRules[fieldName];
      const value = data[fieldName];

      // Required validation
      let isRequired = false;
      if (typeof rules.required === 'function') {
        isRequired = rules.required(data);
      } else if (rules.required) {
        isRequired = true;
      }
      
      if (isRequired && (!value || value.toString().trim() === '')) {
        newErrors[fieldName] = rules.message || `Το πεδίο είναι υποχρεωτικό`;
        continue;
      }

      // Skip other validations if field is empty and not required
      if (!value || value.toString().trim() === '') {
        continue;
      }

      // Min length validation
      if (rules.minLength && value.toString().length < rules.minLength) {
        newErrors[fieldName] = rules.message || `Ελάχιστο μήκος: ${rules.minLength} χαρακτήρες`;
        continue;
      }

      // Max length validation
      if (rules.maxLength && value.toString().length > rules.maxLength) {
        newErrors[fieldName] = rules.message || `Μέγιστο μήκος: ${rules.maxLength} χαρακτήρες`;
        continue;
      }

      // Email validation
      if (rules.email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          newErrors[fieldName] = rules.message || 'Μη έγκυρη διεύθυνση email';
          continue;
        }
      }

      // URL validation
      if (rules.url) {
        try {
          new URL(value);
        } catch {
          newErrors[fieldName] = rules.message || 'Μη έγκυρη διεύθυνση URL';
          continue;
        }
      }

      // Pattern validation
      if (rules.pattern) {
        const regex = new RegExp(rules.pattern);
        if (!regex.test(value)) {
          newErrors[fieldName] = rules.message || 'Μη έγκυρη μορφή';
          continue;
        }
      }

      // Custom validation function
      if (rules.validate && typeof rules.validate === 'function') {
        const customError = rules.validate(value, data);
        if (customError) {
          newErrors[fieldName] = customError;
          continue;
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, validationRules]);

  // Apply formatters to form data
  const applyFormatters = useCallback((data) => {
    const formattedData = { ...data };
    
    Object.keys(formatters).forEach(fieldName => {
      if (formattedData[fieldName] !== undefined && formattedData[fieldName] !== null) {
        formattedData[fieldName] = formatters[fieldName](formattedData[fieldName]);
      }
    });
    
    return formattedData;
  }, [formatters]);

  // Handle form submission (optional submitPayload: cleaned snapshot, avoids stale formData after setState)
  const handleSubmit = useCallback(async (submitPayload) => {
    const payload = submitPayload !== undefined && submitPayload !== null ? submitPayload : formData;
    if (!validate(payload)) {
      return { success: false, errors };
    }

    setLoading(true);
    setSuccess(false);

    try {
      const formattedData = applyFormatters(payload);
      const result = await onSubmit(formattedData);
      
      if (result && result.success) {
        setSuccess(true);
        return { success: true, data: result.data };
      } else {
        // Handle backend validation errors
        const errorMessage = result?.error || 'Σφάλμα κατά την αποθήκευση';
        
        // If result has specific errors object, use it (for field-level validation)
        let fieldErrors = {};
        if (result?.errors && typeof result.errors === 'object') {
          fieldErrors = result.errors;
        } else {
          // Fallback to simple error message mapping
          if (errorMessage.includes('email')) {
            fieldErrors.email = errorMessage;
          } else if (errorMessage.includes('ΑΦΜ')) {
            fieldErrors.vat_number = errorMessage;
          } else if (errorMessage.includes('τηλέφωνο')) {
            fieldErrors.phone = errorMessage;
          } else {
            fieldErrors.general = errorMessage;
          }
        }
        
        setErrors(fieldErrors);
        return { success: false, error: errorMessage };
      }
    } catch (error) {
      console.error('Form submission error:', error);
      
      // Handle HTTP errors with specific messages
      let errorMessage = 'Σφάλμα κατά την αποθήκευση';
      let fieldErrors = {};
      
      if (error.response && error.response.data && error.response.data.detail) {
        const detail = error.response.data.detail;
        
        // Handle Pydantic validation errors (array of errors)
        if (Array.isArray(detail)) {
          console.log('Pydantic validation errors:', detail);
          detail.forEach(err => {
            const fieldPath = err.loc || [];
            const fieldName = fieldPath[fieldPath.length - 1]; // Get the last part of the path
            
            if (fieldPath.includes('allocations')) {
              // Handle allocation-specific errors
              const allocationsIndex = fieldPath.findIndex(path => path === 'allocations');
              const allocationIndex = fieldPath[allocationsIndex + 1];
              const allocationField = fieldPath[allocationsIndex + 2];
              
              // Create error key that matches FormBuilder format
              const errorKey = `allocations.${allocationIndex}.${allocationField}`;
              
              // Translate common validation errors to Greek
              let translatedMessage = err.msg;
              if (err.type === 'int_type') {
                translatedMessage = 'Παρακαλώ επιλέξτε μια έγκυρη υπηρεσία';
              } else if (err.type === 'decimal_type') {
                translatedMessage = 'Παρακαλώ εισάγετε ένα έγκυρο ποσό';
              } else if (err.type === 'missing') {
                translatedMessage = 'Το πεδίο είναι υποχρεωτικό';
              }
              
              fieldErrors[errorKey] = translatedMessage;
            } else if (fieldName) {
              // Handle regular field errors
              fieldErrors[fieldName] = err.msg;
            } else {
              fieldErrors.general = err.msg;
            }
          });
          
          // If no specific field errors, use general message
          if (Object.keys(fieldErrors).length === 0) {
            fieldErrors.general = 'Παρακαλώ διορθώστε τα σφάλματα στη φόρμα';
          }
          
          // Set general error message for display
          errorMessage = 'Παρακαλώ διορθώστε τα σφάλματα στη φόρμα';
          
          console.log('Field errors set:', fieldErrors);
        } else {
          // Handle simple string error messages
          errorMessage = detail;
          
          // Check if it's a field-specific validation error
          if (errorMessage.includes('email')) {
            fieldErrors.email = errorMessage;
          } else if (errorMessage.includes('ΑΦΜ')) {
            fieldErrors.vat_number = errorMessage;
          } else if (errorMessage.includes('τηλέφωνο')) {
            fieldErrors.phone = errorMessage;
          } else {
            fieldErrors.general = errorMessage;
          }
        }
      } else {
        fieldErrors.general = error.message || errorMessage;
      }
      
      setErrors(fieldErrors);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [formData, validate, onSubmit, applyFormatters, errors]);

  // Reset form
  const resetForm = useCallback(() => {
    setFormData(initialValues);
    setErrors({});
    setLoading(false);
    setSuccess(false);
  }, [initialValues]);

  return {
    formData,
    setFormData,
    errors,
    setErrors,
    loading,
    setLoading,
    success,
    setSuccess,
    handleChange,
    handleSubmit,
    validate,
    resetForm
  };
};

export default useForm;